import { AlterableBusinessHeader } from '@/coreDomainsModule/entities/businessExcelInfo';
import { SelectYearMonthPattern } from './UploadFile';
import { REQUIRE_ALTERABLE_HEADERS } from '@/registerInputModule/constants';

/**
 * 取引情報紐付け
 * @param linkedBusinessHeaders - 紐付けのヘッダー
 * @param alternateHeaderInfos - 読み替え可能なヘッダー情報
 * @param inlinedYearMonthInfo - 年月情報の取得パターン（インライン）
 * @param inHeaderYearMonthInfo - 年月情報の取得パターン（ヘッダー）
 */
export interface LinkingInput {
  alternateHeaderInfos: AlterableHeaderInfo[];
  inlinedYearMonthInfo: SelectYearMonthPatternInlined;
  inHeaderYearMonthInfo: SelectYearMonthPatternInHeader;
}

/**
 * エラーの種類
 */
type ValidateError = 'NotSelected' | 'None';

/**
 * 紐付けのヘッダー
 * @param displayName - 表示名
 * @param key - キー
 * @param value - 選択された値を保持する
 */
export interface LinkingInputHeader {
  displayName: string;
  key: AlterableBusinessHeader;
  value?: string;
  required: boolean;
  validateError: ValidateError;
}

/**
 * 初期設定可能なファイルの種類(配列)
 */
export const inputFileTypes = ['Inventory', 'SalesResults', 'Arrival'] as const;

/**
 * 初期設定可能なファイルの種類
 */
export type InputFileType = (typeof inputFileTypes)[number];

/**
 * 読み替え可能なヘッダー
 * @param WarehouseNumber - 倉庫番号
 * @param SupplierNumber - 仕入先番号
 * @param BusinessNumber - 商品番号
 * @param Package - 容量
 * @param CustomerNumber - 売上先番号
 * @param SalesDestinationNumber - 送り先番号
 * @param InventoryValue - 在庫数量
 * @param SalesResultsValue - 販売実績数量
 * @param ArrivalValue - 入荷実績数量
 */
export type AlterableHeader =
  | 'WarehouseNumber'
  | 'Warehouse'
  | 'SupplierNumber'
  | 'SupplierName'
  | 'BusinessNumber'
  | 'BusinessName'
  | 'Package'
  | 'CustomerNumber'
  | 'CustomerName'
  | 'SalesDestinationNumber'
  | 'SalesDestinationName'
  | 'InventoryValue'
  | 'SalesResultsValue'
  | 'ArrivalValue'
  | 'Manufacturer'
  | 'EndUser';

/**
 * 読み替え可能なヘッダーのうち、登録が必須であるヘッダー一覧
 */
export type RequireAlterableHeader = (typeof REQUIRE_ALTERABLE_HEADERS)[number];

/**
 * 高速判定用 Set（実行時データ）
 */
const requiredSet = new Set<RequireAlterableHeader>(REQUIRE_ALTERABLE_HEADERS);

/**
 * RequireAlterableHeaderの型ガード
 * @param header - ヘッダー
 * @returns - ヘッダーがRequireAlterableHeaderであるかどうか
 */
export const isRequireAlterableHeader = (header: AlterableHeader): header is RequireAlterableHeader => {
  return requiredSet.has(header as RequireAlterableHeader);
};

/**
 * 読み替え可能なヘッダー情報
 * @param header - ヘッダー
 * @param value - 値
 * @param unused - 登録しないヘッダーかどうか(undefinedの場合は登録する/しないは調整できない)
 * @param validateError - エラーの種類
 */
export interface AlterableHeaderInfo {
  header: AlterableHeader;
  value: string;
  unused: boolean | undefined;
  validateError: boolean;
}

/**
 * 年月情報の取得パターン（インライン）
 * @param headerName - 取得するヘッダー名
 */
export type SelectYearMonthPatternInlined = {
  headerName: string;
  validateError: SelectYearMonthPatternError;
};

/**
 * 年月情報の取得パターン（ヘッダー）
 * @param startColumnNumber - 開始列番号
 * @param endColumnNumber - 終了列番号
 * @param startYear - 開始年
 * @param startMonth - 開始月
 * @param endYear - 終了年
 * @param endMonth - 終了月
 */
export type SelectYearMonthPatternInHeader = {
  startColumnNumber: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  endColumnNumber: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  start: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
  end: {
    raw: string;
    validateError: SelectYearMonthPatternError;
  };
};

/**
 * 年月情報の取得パターンのエラー
 * @param cellPattern - セルの形式
 * @param sideBySideCell - セルが隣接しているかどうか
 * @param yearMonth - 年月
 * @param sideBySideYearMonth - 年月が隣接しているかどうか
 * @param notEmpty - 空でない
 * @param none - なし
 */
export type SelectYearMonthPatternError = 'cellPattern' | 'sideBySideCell' | 'yearMonth' | 'sideBySideYearMonth' | 'notEmpty' | 'none';

/**
 * 読み替え可能なヘッダーのService
 */
export class AlterableHeaderService {
  static readonly inventoryAlterableHeaderList: AlterableHeader[] = [
    'WarehouseNumber',
    'Warehouse',
    'SupplierNumber',
    'SupplierName',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'InventoryValue',
    'Manufacturer',
  ];

  static readonly salesResultsAlterableHeaderList: AlterableHeader[] = [
    'WarehouseNumber',
    'Warehouse',
    'SupplierNumber',
    'SupplierName',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'CustomerNumber',
    'CustomerName',
    'SalesDestinationNumber',
    'SalesDestinationName',
    'SalesResultsValue',
    'EndUser',
  ];

  static readonly arrivalAlterableHeaderList: AlterableHeader[] = [
    'WarehouseNumber',
    'Warehouse',
    'SupplierNumber',
    'SupplierName',
    'BusinessNumber',
    'BusinessName',
    'Package',
    'ArrivalValue',
    'Manufacturer',
  ];

  /**
   * 可変ヘッダー情報を取得する
   * @param inputFileType - ファイルの種類
   * @param selectYearMonthPattern - 年月情報の取得パターン
   * @param registerExcelInfo - 可変ヘッダー情報
   */
  static readonly getAlterableYearMonthHeaderValue = (
    inputFileType: InputFileType,
    selectYearMonthPattern: SelectYearMonthPattern,
    registerExcelInfo: {
      header: AlterableHeader;
      value: string;
    }[]
  ): string => {
    if (selectYearMonthPattern === 'Inlined') return ''; // Inlinedの場合は可変ヘッダー情報を返さない
    switch (inputFileType) {
      case 'Inventory':
        return registerExcelInfo.find((info) => info.header === 'InventoryValue')?.value ?? '';
      case 'SalesResults':
        return registerExcelInfo.find((info) => info.header === 'SalesResultsValue')?.value ?? '';
      case 'Arrival':
        return registerExcelInfo.find((info) => info.header === 'ArrivalValue')?.value ?? '';
    }
  };
}
